import React, { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Input, TypographyColors, TypographyVariants, useSnackbar } from '@lyearn/ui';

import { useActionItemContext } from '../../context';
import useUpdateActionItem from '../../hooks/useUpdateActionItem';
import { inputStyles } from './styles';

interface EditableInputProps {
  className?: string;
  color?: TypographyColors;
  variant?: TypographyVariants;
  disableRenameMode?: () => void;
}

const EditableInput = ({
  className,
  color = 'text-text-primary',
  variant = 'body-short-01',
  disableRenameMode,
}: EditableInputProps) => {
  const {
    actionItem: { title, id },
  } = useActionItemContext();

  const inputElRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const { updateActionItem } = useUpdateActionItem();
  const [actionItemTitle, setActionItemTitle] = useState<string>(title);
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleBlur = useCallback(() => {
    if (actionItemTitle.trim() !== '' && actionItemTitle.trim() !== title.trim()) {
      updateActionItem({
        data: {
          actionItemId: id,
          title: actionItemTitle,
        },
      })
        .then((res) => {
          if (res.data?.updateActionItem?.actionItem?.id) {
            showSnackbar({
              message: 'Task renamed successfully',
            });
            disableRenameMode?.();
          }
        })
        .catch(() => {
          showErrorSnackbar({
            message: t('Common:SomethingWentWrong'),
          });
        });
    } else {
      disableRenameMode?.();
    }
  }, [
    updateActionItem,
    id,
    actionItemTitle,
    showSnackbar,
    disableRenameMode,
    showErrorSnackbar,
    t,
    title,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleBlur();
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        disableRenameMode?.();
      }
    },
    [handleBlur, disableRenameMode],
  );

  const setInputRef = useCallback((el: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (!el) {
      inputElRef.current = null;
      return;
    }

    if (!inputElRef.current) {
      inputElRef.current = el;
      setTimeout(() => {
        el.focus();
        if (typeof (el as HTMLInputElement).select === 'function') {
          (el as HTMLInputElement).select();
        }
      }, 0);
    } else {
      inputElRef.current = el;
    }
  }, []);

  return (
    <Input
      autoFocus
      className={cn(
        className,
        'group-hover:bg-bg-secondary max-h-24 border-none w-full',
        inputStyles,
      )}
      color={color as TypographyColors}
      inputRef={setInputRef}
      maxRows={1}
      value={actionItemTitle}
      variant={variant as TypographyVariants}
      onBlur={handleBlur}
      onChange={(e) => setActionItemTitle(e.target.value)}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onKeyDown={handleKeyDown}
    />
  );
};

export default React.memo(EditableInput);
